"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.baseStationPill = exports.isBaseStationOffline = exports.getHubPresence = exports.getHubZwaveStatus = exports.isDashboardSensor = exports.getInstallProgressState = exports.isInstallSignedOff = exports.getSensorInstallState = exports.canRetryInclusion = exports.getSensorPillState = exports.SENSOR_PILL_STATES = void 0;
const Hub_1 = require("../types/Hub");
const Sensor_1 = require("../types/Sensor");
/**
 * Every pill state, so a consumer's style map can be checked against the full
 * set: a state the map lacks is `undefined` at render and crashes the screen.
 */
exports.SENSOR_PILL_STATES = [
    'draft',
    'pending',
    'pending_stale',
    'pairing',
    'pairing_failed',
    'paired_awaiting_data',
    'active',
    'inactive',
    'maintenance',
    // A deleted row whose unit is coming off the Z-Wave network, and a re-added
    // unit waiting for that (see getRemovalPillState).
    'removal_waiting_hub',
    'removal_reset_needed',
    'removal_still_joined',
    'removal_in_progress',
    'removed',
    'waiting_for_unpair',
];
const PENDING_STALE_SECONDS = 5 * 60;
function toSeconds(value) {
    if (value === undefined || value === null)
        return 0;
    const parsed = typeof value === 'number' ? value : parseInt(String(value), 10);
    return isNaN(parsed) ? 0 : parsed;
}
// deleteSensor writes lowercase 'deleted'; uppercase compare like isDashboardSensor.
const isDeleted = (sensor) => String(sensor.status ?? '').toUpperCase() === 'DELETED';
/**
 * The installer's next step for a deleted row (G1 / G3 / G4). Until the hub
 * ACKs the latest request the pill follows the installer's answer alone: an
 * ALIVE unit gets the SW2 step at once (the app adds a "waiting for base
 * station" note while receivedAt is unset), a BROKEN one waits on the hub. A
 * refusal of the delete's own try is expected for a live unit, so only a later
 * one, or any refusal of a "broken" unit, means the reset didn't take.
 */
function getRemovalPillState(removal) {
    if (!removal || removal.closedAt)
        return 'removed';
    const broken = removal.reason === 'BROKEN';
    if (!removal.receivedAt)
        return broken ? 'removal_waiting_hub' : 'removal_reset_needed';
    const { result, trigger } = removal;
    const refused = result === 'still_in_network' && (broken || trigger !== 'delete');
    if (refused || result === 'still_joined')
        return 'removal_still_joined';
    return broken ? 'removal_in_progress' : 'removal_reset_needed';
}
/**
 * Derives the UI pill identifier for a sensor. Consumers map this to
 * i18n labels + color tokens. Computed, not a new stored field, so the
 * same status row can render differently with age (e.g. PENDING becomes
 * "Pending" or "Not received — retry" past PENDING_STALE_SECONDS).
 */
function getSensorPillState(sensor, nowSeconds) {
    const now = nowSeconds ?? Math.floor(Date.now() / 1000);
    const last = toSeconds(sensor.lastReported);
    if (isDeleted(sensor))
        return getRemovalPillState(sensor.removal);
    if (sensor.heldForRemovalOf)
        return 'waiting_for_unpair';
    switch (sensor.status) {
        case Sensor_1.SensorStatus.Draft:
            return 'draft';
        case Sensor_1.SensorStatus.Active:
            return 'active';
        case Sensor_1.SensorStatus.Inactive:
            return 'inactive';
        case Sensor_1.SensorStatus.Maintenance:
            return 'maintenance';
        case Sensor_1.SensorStatus.Pairing:
            return 'pairing';
        case Sensor_1.SensorStatus.PairingFailed:
            return 'pairing_failed';
        case Sensor_1.SensorStatus.PairedAwaitingData:
            return 'paired_awaiting_data';
        case Sensor_1.SensorStatus.Pending:
            return last > 0 && now - last > PENDING_STALE_SECONDS ? 'pending_stale' : 'pending';
        default:
            return 'pending';
    }
}
exports.getSensorPillState = getSensorPillState;
/**
 * True when the pill should be considered "actionable" — the installer
 * can tap Retry to re-fire the inclusion flow. Not for a row the base station
 * never acked (pending_stale): its fix is the Pairing tab's "Send again", which
 * re-sends the add (the cloud does only that for a retry on a Pending or
 * Inactive row).
 */
function canRetryInclusion(state) {
    return state === 'pairing_failed';
}
exports.canRetryInclusion = canRetryInclusion;
/**
 * Distinguishes "never finished installing" from "installed once, now
 * silent" — two problems with different fixes (re-run inclusion vs
 * batteries/power/radio). INACTIVE alone can't tell them apart because the
 * status sweep also ages never-paired PENDING rows into INACTIVE; a real
 * prior life is evidenced by lastReported, which only inclusion acks and
 * data reports ever bump.
 */
function getSensorInstallState(sensor, nowSeconds) {
    const pill = getSensorPillState(sensor, nowSeconds);
    if (pill === 'active')
        return 'active';
    if ((pill === 'inactive' || pill === 'maintenance') && toSeconds(sensor.lastReported) > 0) {
        return 'offline';
    }
    return 'not_installed';
}
exports.getSensorInstallState = getSensorInstallState;
/**
 * Whether an installer has signed this install off — the single source of
 * truth for the install progress bar.
 *
 * Deliberately NOT derived from status or `lastReported`. A sensor reports
 * from a bench, from a box, from the wrong room; the base station acking an
 * inclusion proves less still. The only thing that means "this install is
 * finished" is the person who did it saying so, which is what
 * `completeSensorInstall` stamps.
 */
function isInstallSignedOff(sensor) {
    return !isDeleted(sensor) && toSeconds(sensor.installCompletedAt) > 0;
}
exports.isInstallSignedOff = isInstallSignedOff;
/**
 * Where a sensor sits in the install job, for the installer's roster.
 *
 *   not_started  scanned into the app, never handed to the hub (DRAFT).
 *   in_progress  handed over — pairing, waiting on data, or finished pairing
 *                and left with "Finish later". Resumable.
 *   signed_off   an installer tapped Finish on a green checklist.
 *   removing     deleted, its unit not yet off the network: its own to-do.
 *   removed      deleted and done (or deleted before removals existed).
 *
 * Separate from `getSensorInstallState`, which answers the homeowner's
 * question (is data flowing?) rather than the installer's (is the job done?).
 * A sensor can be reporting happily and still be an unfinished install.
 */
function getInstallProgressState(sensor) {
    if (isDeleted(sensor))
        return sensor.removal && !sensor.removal.closedAt ? 'removing' : 'removed';
    if (isInstallSignedOff(sensor))
        return 'signed_off';
    if (String(sensor.status ?? '').toUpperCase() === Sensor_1.SensorStatus.Draft)
        return 'not_started';
    return 'in_progress';
}
exports.getInstallProgressState = getInstallProgressState;
// Statuses hidden from homeowner-facing dashboards/lists. Pre-inclusion
// lifecycle states are installer concerns (failed pairing attempts linger in
// the table forever), and 'deleted' covers soft-deleted rows that can slip in
// via the onSensorUpdated subscription mid-session (deleteSensor writes
// status='deleted', and the row stays in the local array until refetch).
// Uppercase compare so legacy lowercase rows ('pending') match too.
const HIDDEN_DASHBOARD_STATUSES = new Set([
    'DELETED',
    Sensor_1.SensorStatus.Draft,
    Sensor_1.SensorStatus.Pending,
    Sensor_1.SensorStatus.Pairing,
    Sensor_1.SensorStatus.PairingFailed,
]);
/**
 * Whether a sensor belongs on homeowner-facing surfaces (dashboard cards,
 * detail-screen sensor lists, aggregation averages). Installer screens
 * (SensorSettings pills) intentionally bypass this and show everything.
 *
 * Never-paired rows are hidden too. `HIDDEN_DASHBOARD_STATUSES` catches the
 * pre-inclusion lifecycle states, but not an INACTIVE row the sweep aged out
 * of PENDING — that row never reported once, and it used to reach homeowner
 * dashboards wearing an "Offline" label, telling someone a sensor had gone
 * quiet when it had never been installed. Installers and web-admin still see
 * it, labelled "Never paired".
 */
function isDashboardSensor(sensor) {
    if (HIDDEN_DASHBOARD_STATUSES.has(String(sensor.status ?? '').toUpperCase()))
        return false;
    const isInactive = String(sensor.status ?? '').toUpperCase() === Sensor_1.SensorStatus.Inactive;
    if (isInactive && toSeconds(sensor.lastReported) <= 0)
        return false;
    return true;
}
exports.isDashboardSensor = isDashboardSensor;
/**
 * The Sensor Settings banner (G3): the hub reports zwave-status when a removal
 * can't run because ZPC is down or the radio is deaf, and `ok` once it
 * recovers; processHubZwaveStatus stores it as Hub.config.zwave. null when
 * fine, never reported, or the config doesn't parse.
 */
function getHubZwaveStatus(hub) {
    try {
        const state = JSON.parse(hub?.config ?? '{}')?.zwave?.state;
        return state === 'zpc_down' || state === 'radio_down' ? state : null;
    }
    catch {
        return null;
    }
}
exports.getHubZwaveStatus = getHubZwaveStatus;
/**
 * The connection the cloud stores on a base station (Hub.config.connectivity,
 * from AWS IoT lifecycle events). undefined with no record: a base station
 * that hasn't reconnected since that shipped, or a gateway read without its
 * config.
 */
function getHubPresence(hub) {
    try {
        const state = JSON.parse(hub?.config ?? '{}')?.connectivity?.state;
        return state === 'connected' || state === 'disconnected' ? state : undefined;
    }
    catch {
        return undefined;
    }
}
exports.getHubPresence = getHubPresence;
/**
 * The base station's MQTT session is down. No presence record is not
 * "offline", so the app keeps offering what needs the base station.
 */
const isBaseStationOffline = (hub) => getHubPresence(hub) === 'disconnected';
exports.isBaseStationOffline = isBaseStationOffline;
/**
 * A base station's two flags read together (Aidan, 10-13): the connection, and
 * whether data flows (Hub.status). Maintenance wins over both. Otherwise
 * disconnected is offline whatever Hub.status says, as Hub.status lags a drop
 * by 30-45 min. Connected but INACTIVE is "no data" only with live sensors
 * (radio or sensors): with none, nothing is expected. No presence record:
 * Hub.status alone.
 */
function baseStationPill(hub, hasLiveSensors = false) {
    if (hub.status === Hub_1.HubStatus.Maintenance)
        return 'maintenance';
    const presence = getHubPresence(hub);
    if (presence === 'disconnected')
        return 'offline';
    if (presence === 'connected') {
        return hasLiveSensors && hub.status === Hub_1.HubStatus.Inactive ? 'no_data' : 'online';
    }
    if (hub.status === Hub_1.HubStatus.Active)
        return 'online';
    if (hub.status === Hub_1.HubStatus.Inactive)
        return 'offline';
    return 'pending';
}
exports.baseStationPill = baseStationPill;
